import React, { FC, useContext, useEffect, useState } from 'react';
import {
  alpha,
  Box,
  Button,
  createTheme,
  hexToRgb,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SurveyService from '../../services/SurveyService';
import { ISurvey } from '../../shared/interfaces/ISurvey';
import { LocalTranslationContext } from '../../hooks/useLocalTranslation';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import CustomQuestionDropdown from './CustomQuestionDropdown';
import CustomQuestionSingle from './CustomQuestionSingle';
import CustomQuestionText from './CustomQuestionText';
import CustomQuestionScalenps from './CustomQuestionScalenps';
import Autolink from '../Autolink/Autolink';
import { SurveyProgress } from '../SurveyProgress/SurveyProgress';
import CustomQuestionMultiple from './CustomQuestionMultiple';
import { SurveyRateUsButton } from '../SurveyRateUsButton/SurveyRateUsButton';
import CssUtils from '../../shared/CssUtils';
import CustomQuestionMatrix from './CustomQuestionMatrix';
import {
  buildClearedMatrixAnswer,
  buildMatrixAnswer,
  getMatrixSelections,
  isMatrixComplete,
  matrixAnswerKey,
} from '../../shared/helpers/SurveyMatrix';
import { useIsSurveyPreview } from '../../hooks/useIsSurveyPreview';

const CustomSurveyContent: FC<ISurvey & { selectedLanguage: string }> = (
  props: ISurvey & Record<string, any>
) => {
  const {
    primaryColor,
    secondaryColor,
    platform,
    platformUrl,
    platformName,
    id,
    questions,
    title,
    titleNegative,
    outroText,
    outroTextNegative,
    outroDisabled,
    introDisabled,
    customFont,
    customFontFormat,
    score,
    introText,
    feedbackActive,
    feedbackTitle,
    feedbackText,
    answers,
    contactEmail,
    progressBar,
    lang,
    selectedLanguage,
  } = props;

  const theme = useTheme();
  const isPreview = useIsSurveyPreview();
  const localContext = useContext(LocalTranslationContext);
  const { t: globalT } = useTranslation('customSurvey');
  const t = localContext ? localContext.t : globalT;
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [responseId, setResponseId] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<number>(getStartStep());
  const [actionCount, setActionCount] = useState<number>(0);
  const [currentAnswer, setCurrentAnswer] = useState<string | string[]>('');
  const { setCurrentView } = useContext(CurrentViewContext);
  const [submitCount, setSubmitCount] = useState<number>(0);
  const [finished, setFinished] = useState<boolean>(false);
  const [answersObject, setAnswersObject] = useState<any>({});
  const [disableNext, setDisableNext] = useState<boolean>(false);
  const [currentLang, setCurrentLang] = useState<string>(selectedLanguage);

  const getTranslatedString = (key: string) => {
    return props.i18n?.[selectedLanguage]?.[key] ?? props[key] ?? '';
  };

  const hasAnswer = (questionId: string) => {
    if (answersObject[questionId] && answersObject[questionId].feedback) {
      return true;
    }
    return false;
  };

  const isQuestionAnswered = (question: NonNullable<typeof questions>[number]) =>
    question.type === 'CUSTOMMATRIX'
      ? isMatrixComplete(answersObject, question.questionId || '', question.rows ?? [])
      : hasAnswer(question.questionId || '');

  function getStartStep() {
    if (introDisabled) {
      return 0;
    }
    return -1;
  }

  const getAnswer = (questionId: string) => {
    if (answersObject[questionId]) {
      return answersObject[questionId].feedback;
    }
    return '';
  };

  const setAnswer = (
    questionId: string,
    value: string | string[],
    step: number,
    additionalData?: Record<string, boolean | string>
  ) => {
    setCurrentAnswer(value);
    setAnswersObject((prev: any) => {
      const newAnswers = prev;

      if (!newAnswers[questionId]) {
        newAnswers[questionId] = {
          questionId: questionId,
          question: '',
          answer: 1,
          feedback: value,
          language: selectedLanguage ?? lang,
          ...additionalData,
        };
      } else {
        newAnswers[questionId] = {
          ...newAnswers[questionId],
          feedback: value,
          ...additionalData,
        };
      }
      return newAnswers;
    });
  };

  useEffect(() => {
    if (submitCount > 0) {
      try {
        (async () => {
          const sendAnswers: any[] = [];
          Object.keys(answersObject).forEach((key: string) => {
            sendAnswers.push(answersObject[key]);
          });
          if (responseId) {
            sendAnswers.forEach((sa: any) => (sa.responseId = responseId));
          }
          if (sendAnswers.length > 0) {
            if (!isPreview) {
              const answers = await SurveyService.saveCustomSurveyAnswer(id, sendAnswers);
              if (answers.length > 0) {
                setResponseId(answers[0].ResponseID);
              }
            }
          }
        })();
      } catch (error) {
        console.error('Error saving survey:', error);
      }
    }
  }, [submitCount, isPreview]);

  const saveSurvey = async () => {
    setSubmitCount(submitCount + 1);
  };

  useEffect(() => {
    setCurrentView(t('survey'));
    document.title = `${t('app_branding')} ${t('survey')}`;
  }, [setCurrentView, t]);

  const showLastPage = () => {
    const questionCount = questions?.length || 0;
    return questionCount === currentStep;
  };

  const isLastQuestion = (step?: number) => {
    let questionStep;
    if (step) {
      questionStep = step;
    } else {
      questionStep = currentStep;
    }
    const questionCount = questions?.length || 0;
    const isLast = questionStep === questionCount - 1;
    return isLast;
  };

  const canGoBack = () => {
    return getStartStep() !== currentStep && !finished;
  };

  const canGoNext = () => {
    return !showLastPage() && !finished;
  };

  const getFontFormatByFileExtension = (fontFormat: FontFileExtensions) => {
    return {
      woff2: 'woff2',
      woff: 'woff',
      ttf: 'truetype',
    }[fontFormat];
  };

  const customTheme = createTheme({
    palette: {
      primary: {
        main: primaryColor ? primaryColor : theme.palette.primary.main,
      },
      divider: secondaryColor ? alpha(hexToRgb(secondaryColor), 0.2) : theme.palette.divider,
    },
    typography: {
      fontWeightBold: 700,
      fontFamily: customFont && customFontFormat ? `"${id}"` : '"Arial", "Helvetica", sans-serif',
    },
    components: {
      MuiCssBaseline: {
        styleOverrides:
          customFont && customFontFormat
            ? `
           @font-face {
            weight: 700;
            font-family: "${id}";
            src: url(${customFont}) format("${getFontFormatByFileExtension(
                customFontFormat as FontFileExtensions
              )}");
           }
        `
            : undefined,
      },
    },
  });

  const goNext = async (step: number): Promise<void> => {
    setActionCount(actionCount + 3);

    if (questions && questions[step + 1]?.type === 'CUSTOMFEEDBACK' && isUserSentimentPositive()) {
      setAnswer(questions[step + 1].questionId, '', step);
      return await goNext(step + 1);
    }
    if (step > -1) {
      await saveSurvey();
    }
    if (isLastQuestion(step)) {
      setFinished(true);
      if (outroDisabled && platformUrl && isUserSentimentPositive()) {
        void (async () => {
          if (!isPreview) {
            await SurveyService.saveSurveyPlatformClickBeforeNavigation(
              id,
              'customsurvey',
              platformUrl
            );
          }
          window.location.assign(platformUrl);
        })();
        return;
      }
    }
    setCurrentStep(step + 1);
  };

  const goBack = (step: number): undefined => {
    setActionCount(actionCount + 7);

    if (questions && questions[step - 1]?.type === 'CUSTOMFEEDBACK' && isUserSentimentPositive()) {
      return goBack(step - 1);
    }

    setCurrentStep(step - 1);
  };

  const handleQuestionChange = async (
    questionId: string,
    value: string | string[],
    step: number,
    proceed: boolean,
    disableNext: boolean = false,
    additionalData?: Record<string, boolean | string>
  ) => {
    setDisableNext(disableNext);

    console.log('additionalData: ', additionalData);
    if (typeof value == 'object') {
      value = JSON.stringify(value);
    }
    // this causes the form to refresh and show the next button in the correct state
    setActionCount(actionCount + 1);
    setAnswer(questionId, value, step, additionalData);
    if (proceed) {
      goNext(step);
    }
  };

  const handleMatrixChange = (
    questionId: string,
    options: any[],
    rowId: string,
    columnIndex: number
  ) => {
    setDisableNext(false);
    setActionCount(actionCount + 1);
    setAnswersObject((prev: any) => ({
      ...prev,
      [matrixAnswerKey(questionId, rowId)]: buildMatrixAnswer(
        questionId,
        rowId,
        options,
        columnIndex,
        selectedLanguage ?? lang
      ),
    }));
  };

  const handleMatrixClear = (questionId: string, rowId: string) => {
    setActionCount(actionCount + 1);
    setAnswersObject((prev: any) => ({
      ...prev,
      [matrixAnswerKey(questionId, rowId)]: buildClearedMatrixAnswer(
        questionId,
        rowId,
        selectedLanguage ?? lang
      ),
    }));
  };

  const isUserSentimentPositive = () => {
    if (!questions) return true;

    const gateQuestions = questions.filter((q) => q.type === 'CUSTOMGATE');

    if (gateQuestions.length == 0) {
      return true;
    }

    const gateQuestionSentiments = gateQuestions.map(
      (q) => answersObject[q.questionId]?.feedback === 'yes'
    );
    return gateQuestionSentiments.some(Boolean);
  };

  const isMatrixStep = () => questions?.[currentStep]?.type === 'CUSTOMMATRIX';

  const renderQuestion = (step: number) => {
    if (!questions || !questions[step]) {
      return null;
    }
    const question = questions[step];
    return (
      <ThemeProvider theme={customTheme}>
        <Box
          className={`survey-question-step survey-question-step--custom survey-question-step--${(
            question.type ?? ''
          ).toLowerCase()}`}
          key={question.questionId}
          sx={question.type === 'CUSTOMMATRIX' ? { alignSelf: 'stretch' } : undefined}
        >
          <Typography
            className="survey-title survey-question-title"
            variant="h5"
            sx={{
              color: primaryColor,
              marginBottom: theme.spacing(5),
              alignItems: 'center',
              fontWeight: 'bold',
              fontSize: theme.spacing(4),
            }}
          >
            {getTranslatedString('title')}
          </Typography>
          <Typography
            className="survey-question-text"
            variant="h5"
            sx={{
              color: secondaryColor,
              marginBottom: theme.spacing(3),
              alignItems: 'center',
              whiteSpace: 'pre-wrap',
            }}
          >
            {question.questionI18n?.[selectedLanguage] ?? question.question}
          </Typography>
          {'CUSTOMDROPDOWN' == (question.type ?? '') && (
            <CustomQuestionDropdown
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              step={step}
              selectedLanguage={selectedLanguage}
            />
          )}
          {'CUSTOMSCALENPS' == (question.type ?? '') && (
            <CustomQuestionScalenps
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              step={step}
              currentLanguage={selectedLanguage}
            />
          )}
          {'CUSTOMYESNO' == (question.type ?? '') && (
            <CustomQuestionSingle
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              step={step}
              selectedLanguage={selectedLanguage}
            />
          )}
          {'CUSTOMSINGLE' == (question.type ?? '') && (
            <CustomQuestionSingle
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              step={step}
              selectedLanguage={selectedLanguage}
            />
          )}
          {'CUSTOMGATE' == (question.type ?? '') && (
            <CustomQuestionSingle
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              step={step}
              selectedLanguage={selectedLanguage}
            />
          )}
          {'CUSTOMFEEDBACK' == (question.type ?? '') && (
            <CustomQuestionText
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              step={step}
              selectedLanguage={selectedLanguage}
            />
          )}
          {'CUSTOMMULTIPLE' == (question.type ?? '') && (
            <CustomQuestionMultiple
              question={question}
              onChange={handleQuestionChange}
              step={step}
              selectedLanguage={selectedLanguage}
            />
          )}
          {'CUSTOMMATRIX' === (question.type ?? '') && (
            <CustomQuestionMatrix
              question={question}
              selections={getMatrixSelections(
                answersObject,
                question.questionId || '',
                question.rows ?? []
              )}
              onSelect={(rowId, columnIndex) =>
                handleMatrixChange(
                  question.questionId || '',
                  question.options ?? [],
                  rowId,
                  columnIndex
                )
              }
              onClear={
                question.optional
                  ? (rowId) => handleMatrixClear(question.questionId || '', rowId)
                  : undefined
              }
              selectedLanguage={selectedLanguage}
            />
          )}
          {'CUSTOMTEXT' == (question.type ?? '') && (
            <CustomQuestionText
              value={getAnswer(question.questionId || '')}
              question={question}
              onChange={handleQuestionChange}
              step={step}
              selectedLanguage={selectedLanguage}
            />
          )}
        </Box>
        <Box
          className="survey-navigation survey-navigation--custom"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            textAlign: 'center',
            width: '100%',
            gap: theme.spacing(2),
            mt: theme.spacing(4),
          }}
        >
          {canGoBack() && (
            <Button
              className="survey-button survey-button--back"
              variant="contained"
              sx={{
                backgroundColor: primaryColor,
                width: '120px',
                padding: theme.spacing(2),
              }}
              onClick={() => goBack(step)}
            >
              {t('back')}
            </Button>
          )}
          {canGoNext() && (
            <Button
              className={`survey-button survey-button--next survey-button--custom-question survey-button--custom-step-${
                step + 1
              }`}
              variant="contained"
              disabled={disableNext || (!question.optional && !isQuestionAnswered(question))}
              sx={{
                backgroundColor: primaryColor,
                width: '120px',
                padding: theme.spacing(2),
                marginLeft: canGoBack() ? '' : 'auto',
              }}
              onClick={async () => {
                if (isLastQuestion()) {
                  await saveSurvey();
                }
                goNext(step);
              }}
            >
              {t('next')}
            </Button>
          )}
        </Box>
      </ThemeProvider>
    );
  };

  const renderOutro = () => {
    return (
      <Box
        className="survey-outro survey-outro--custom"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Typography
          className="survey-title survey-outro-title"
          variant="h4"
          sx={{
            color: primaryColor,
            marginBottom: theme.spacing(5),
            fontWeight: 'bold',
          }}
        >
          {getTranslatedString('title')}
        </Typography>
        <Typography
          className="survey-outro-text"
          variant="h5"
          sx={{
            color: secondaryColor,
            marginBottom: theme.spacing(5),
            alignItems: 'center',
          }}
        >
          {!outroDisabled ? (
            <>
              {isUserSentimentPositive() ? (
                <Autolink text={getTranslatedString('outroText')} />
              ) : (
                <Autolink
                  text={
                    getTranslatedString('outroTextNegative')
                      ? getTranslatedString('outroTextNegative')
                      : getTranslatedString('outroText')
                  }
                />
              )}
            </>
          ) : (
            <>{t('answers_saved')}</>
          )}
        </Typography>
        {platformUrl && isUserSentimentPositive() && (
          <>
            <Box
              className="survey-rate-us-wrapper"
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: 'fit-content',
                margin: '0 auto',
                mb: 4,
              }}
            >
              <SurveyRateUsButton
                surveyId={id}
                surveyType={'customsurvey'}
                platformUrl={platformUrl}
                platform={platform ?? ''}
                platformName={platformName}
                theme={theme}
                primary={theme.palette.primary.main}
              ></SurveyRateUsButton>
            </Box>
          </>
        )}
      </Box>
    );
  };

  const renderIntro = () => {
    return (
      <>
        <Box
          className="survey-intro survey-intro--custom"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography
            className="survey-title survey-intro-title"
            variant="h4"
            sx={{
              color: primaryColor,
              marginBottom: theme.spacing(5),
              fontWeight: 'bold',
            }}
          >
            {getTranslatedString('title')}
          </Typography>
          <Typography
            className="survey-intro-text"
            variant="h5"
            sx={{
              color: secondaryColor,
              marginBottom: theme.spacing(5),
              alignItems: 'center',
            }}
          >
            <Autolink text={getTranslatedString('introText')} />
          </Typography>
        </Box>
        <Box
          className="survey-navigation survey-navigation--intro"
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            textAlign: 'center',
            width: '100%',
            gap: theme.spacing(2),
            mt: theme.spacing(4),
          }}
        >
          <Button
            className="survey-button survey-button--next survey-button--custom-intro"
            variant="contained"
            sx={{
              backgroundColor: primaryColor,
              width: '120px',
              padding: theme.spacing(2),
            }}
            onClick={async () => {
              if (isLastQuestion()) {
                await saveSurvey();
              }
              goNext(currentStep);
            }}
          >
            {t('next')}
          </Button>
        </Box>
      </>
    );
  };

  return (
    <Box
      className="survey-content survey-content--custom"
      sx={{
        ...CssUtils.surveyContent(),
        // The content box shrinks to fit, so a wide matrix would push it past the screen edge. On a matrix
        // page it takes the available width instead and the matrix scrolls sideways inside it.
        ...(isMatrixStep() && { width: '100%', boxSizing: 'border-box' }),
      }}
    >
      {currentStep === -1 ? renderIntro() : null}
      {renderQuestion(currentStep)}
      {showLastPage() && finished ? renderOutro() : null}

      {progressBar && (
        <SurveyProgress
          introDisabled={introDisabled}
          currentPage={currentStep}
          numPages={questions?.length ?? 0}
          language={lang}
        ></SurveyProgress>
      )}
    </Box>
  );
};

export default CustomSurveyContent;
